import { useState, useEffect, FormEvent } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import * as authApi from '../api/auth'
import { useAuth } from '../hooks/useAuth'
import GoogleSignInButton from '../components/GoogleSignInButton'
import InviteCodeModal from '../components/InviteCodeModal'
import { getDemoMessagesUsed, clearDemoSession } from '../utils/demo'
import { consumePendingCheckout } from '../utils/pendingCheckout'
import { setPendingIndustry } from '../utils/pendingIndustry'
import { trackSignup, setUserData } from '../utils/analytics'
import BetaBanner from '../components/BetaBanner'

export default function Register() {
  const { setUser } = useAuth()
  const navigate = useNavigate()
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [inviteCode, setInviteCode] = useState('')
  const [codeStatus, setCodeStatus] = useState<'valid' | 'invalid' | null>(null)
  const [codeChecking, setCodeChecking] = useState(false)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [searchParams] = useSearchParams()
  const demoMessagesUsed = getDemoMessagesUsed()

  // Industry landing pages link here as /register?industry=<key>. The landing
  // page also writes it to sessionStorage on click, but re-reading the param
  // covers a direct hit on the URL (shared link, bookmark, restored tab).
  // setPendingIndustry ignores anything not in the known industry set.
  useEffect(() => {
    const industry = searchParams.get('industry')
    if (industry) setPendingIndustry(industry)
  }, [searchParams])

  async function handleValidateCode() {
    if (!inviteCode.trim()) return
    setCodeChecking(true)
    try {
      const result = await authApi.validateInviteCode(inviteCode)
      setCodeStatus(result.valid ? 'valid' : 'invalid')
    } catch {
      setCodeStatus('invalid')
    } finally {
      setCodeChecking(false)
    }
  }

  async function handleGoogleSuccess(credential: string) {
    setLoading(true)
    setErrors({})
    try {
      const data = await authApi.googleAuth(credential)
      localStorage.setItem('access_token', data.access)
      localStorage.setItem('refresh_token', data.refresh)
      clearDemoSession()
      setUser(data.user)
      setUserData(data.user?.email)
      // Only fire for a brand-new account (Google auth is shared with login).
      if (data.is_new_user) trackSignup('google')
      if (data.is_new_user || !data.user.onboarding_complete) {
        if (data.is_new_user) {
          setShowInviteModal(true)
        } else {
          navigate('/onboarding')
        }
      } else {
        const next = consumePendingCheckout()
        navigate(next || '/dashboard')
      }
    } catch {
      setErrors({ general: 'Google sign-in failed. Please try again.' })
    } finally {
      setLoading(false)
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setErrors({})
    setLoading(true)
    try {
      const result = await authApi.register(username, email, password)
      localStorage.setItem('access_token', result.access)
      localStorage.setItem('refresh_token', result.refresh)
      clearDemoSession()
      // Enhanced conversions: hand GA4 the email before the conversion fires.
      setUserData(email)
      // Clean signup conversion — register() only succeeds for a NEW account,
      // so this fires exactly once per real email signup.
      trackSignup('email')
      if (inviteCode.trim()) {
        try {
          const codeToApply = inviteCode.trim().toUpperCase()
          const validation = await authApi.validateInviteCode(codeToApply)
          if (validation.valid) {
            const applyResult = await authApi.applyInviteCode(codeToApply)
            if (applyResult.success) {
              try {
                const freshProfile = await authApi.getProfile()
                result.user = { ...result.user, ...freshProfile }
              } catch {
                // non-blocking
              }
            }
          } else {
            console.warn('Invite code invalid after registration:', validation.error)
          }
        } catch (err) {
          console.error('Invite code apply failed:', err)
        }
      }
      setUser(result.user)
      if (inviteCode.trim() && !result.user?.has_beta_access) {
        navigate('/onboarding', { state: { inviteCodeWarning: true } })
      } else {
        navigate('/onboarding')
      }
    } catch (err: any) {
      if (err.response?.data) {
        setErrors(err.response.data)
      } else {
        setErrors({ general: 'Something went wrong. Please try again.' })
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
    <BetaBanner />
    <InviteCodeModal
      isOpen={showInviteModal}
      onClose={() => { setShowInviteModal(false); navigate('/onboarding') }}
      onSuccess={() => { setShowInviteModal(false); navigate('/onboarding') }}
    />
    <div className="auth-page">
      <div className="auth-card">
        <Link to="/" className="auth-back-home">← Back to Home</Link>
        <div className="auth-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}><img src="/owl-mascot.png" alt="Aspired AI" style={{ height: '24px', width: '24px', objectFit: 'contain', borderRadius: '4px', verticalAlign: 'middle', marginRight: '4px', display: 'inline-block' }} /> Aspired AI</div>
        <h1>Create your account</h1>
        {demoMessagesUsed > 0 ? (
          <p className="auth-sub">
            {demoMessagesUsed >= 3
              ? 'Ready to unlock your full AI team?'
              : `You're ${demoMessagesUsed} message${demoMessagesUsed === 1 ? '' : 's'} in — sign up to keep going`}
          </p>
        ) : (
          <p className="auth-sub">Get started with 15 free messages</p>
        )}

        <GoogleSignInButton
          onSuccess={handleGoogleSuccess}
          onError={(err) => setErrors({ general: err })}
          text="signup_with"
        />

        <div className="auth-divider"><span>or</span></div>

        <form onSubmit={handleSubmit} className="auth-form">
          {errors.general && <div className="auth-error">{errors.general}</div>}
          <label>
            Username
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
            />
            {errors.username && <span className="field-error">{errors.username}</span>}
          </label>
          <label>
            Email
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            {errors.email && (
              <span className="field-error">
                {errors.email.includes('Try signing in') ? (
                  <>An account with this email already exists. <Link to="/login">Try signing in instead.</Link></>
                ) : errors.email}
              </span>
            )}
          </label>
          <label>
            Password
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
            {errors.password && <span className="field-error">{errors.password}</span>}
          </label>
          <label>
            Invite Code <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '13px' }}>(optional)</span>
            <div style={{ position: 'relative', display: 'flex', gap: 8 }}>
              <input
                type="text"
                placeholder="e.g. BETA2026"
                value={inviteCode}
                onChange={(e) => {
                  setInviteCode(e.target.value.toUpperCase())
                  setCodeStatus(null)
                }}
                style={{ textTransform: 'uppercase', flex: 1 }}
              />
              {inviteCode && codeStatus !== 'valid' && (
                <button
                  type="button"
                  className="btn-outline"
                  style={{ whiteSpace: 'nowrap', padding: '0 12px' }}
                  onClick={handleValidateCode}
                  disabled={codeChecking}
                >
                  {codeChecking ? '…' : 'Check'}
                </button>
              )}
            </div>
            {codeStatus === 'valid' && (
              <span className="field-success" style={{ color: '#10B981', fontSize: '13px', marginTop: '4px', display: 'block' }}>
                ✓ Valid invite code — you'll get full beta access!
              </span>
            )}
            {codeStatus === 'invalid' && (
              <span className="field-error">Invalid or expired code.</span>
            )}
          </label>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Creating account…' : 'Create account'}
          </button>
          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center', margin: '12px 0 0' }}>
            By creating an account you agree to our{' '}
            <Link to="/terms" style={{ color: 'var(--accent-purple)', textDecoration: 'none' }}
              onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
              onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
            >Terms of Service</Link>
            {' '}and{' '}
            <Link to="/privacy" style={{ color: 'var(--accent-purple)', textDecoration: 'none' }}
              onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
              onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
            >Privacy Policy</Link>
          </p>
        </form>

        <p className="auth-footer">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
    </>
  )
}
